<script setup>
import CommissionNote from '~/components/marketing/CommissionNote.vue';
import {computed, ref, watch, onMounted, onUnmounted, nextTick} from "vue";
import MainFooter from "~/components/layout/MainFooter";
import MainMenu from "~/components/navigation/MainMenu";
import {useNavigationItems} from "~/composables/useNavigationItems";
import UserTopRight from "~/components/navigation/UserTopRight.vue";
import {useAuthService} from "~/composables/useAuthService";
import NoResults from "~/components/loaders/NoResults.vue";
import ClubSearchList from "~/components/clubs/ClubSearchList.vue";
import PeopleSearchResults from "~/components/follows/PeopleSearchResults.vue";
import MfaOfferPrompt from "~/components/security/MfaOfferPrompt.vue";
import PlayerList from "~/components/players/PlayerList.vue";

import {useSignalR} from "~/composables/useSignalR";
import {useTimelineFeed} from "~/composables/useTimelineFeed.js";
import {useMessaging} from "~/composables/useMessaging";

const props = defineProps({
  footer: {
    type: Boolean,
    default: true
  }
});

const {mainMenuItems} = useNavigationItems();
const {isAuthenticated, isSuperUser, user} = useAuthService();
const { unreadCount } = useMessaging();

const drawer = ref(false);

// Mobile drawer presentation: give each menu item an icon, and split the
// primary nav from the sign in / sign up actions (shown as buttons at the foot).
const drawerIcons = {
  Home: 'mdi-home-outline',
  Clubs: 'mdi-account-group-outline',
  Sports: 'mdi-whistle-outline',
  Formats: 'mdi-shape-outline',
  Features: 'mdi-star-outline',
  About: 'mdi-information-outline',
  Help: 'mdi-lifebuoy',
  Contact: 'mdi-email-outline',
};
const drawerIcon = (item) => drawerIcons[item.title] || item.icon || 'mdi-chevron-right';
const drawerNav = computed(() => mainMenuItems.value.filter((i) => i.title !== 'Sign in' && i.title !== 'Sign up'));
const drawerSignUp = computed(() => mainMenuItems.value.find((i) => i.title === 'Sign up')?.link || '/register');
const drawerSignIn = computed(() => mainMenuItems.value.find((i) => i.title === 'Sign in')?.link || '/login');
const router = useRouter();

const openMessaging = () => {
  router.push({ query: { ...route.query, messaging: 'open' } });
};


const {isLoading, error, progress} = useLoadingIndicator();

const searching = ref(false);
const searchValue = ref(null);
// 'clubs' or 'people' from the one search button (People = a friend's exact
// user tag, PRIVACY_PLAN.md §15); 'players' is the super admin's name/email
// search behind its own button. An under-18 cannot follow, so no People.
const searchType = ref('clubs');
const searchInput = ref(null);
const searchIsJunior = computed(() => !!user.value?.isJunior || user.value?.under18 === true);
const searchModes = computed(() => searchIsJunior.value ? [] : [
  {value: 'clubs', label: 'Clubs', icon: 'mdi-account-group-outline', hint: 'By name'},
  {value: 'people', label: 'People', icon: 'mdi-at', hint: 'By user tag, or by name in your clubs'},
]);
const currentMode = computed(() => searchModes.value.find(m => m.value === searchType.value) || searchModes.value[0]);
// The last thing searched for is the next default, per browser (a convenience, so localStorage).
const SEARCH_TYPE_KEY = 'tl.searchType';
const rememberedSearchType = () => {
  try {
    const v = window.localStorage.getItem(SEARCH_TYPE_KEY);
    return v === 'people' || v === 'clubs' ? v : 'clubs';
  } catch {
    return 'clubs';
  }
};
const openSearch = () => {
  searchType.value = searchIsJunior.value ? 'clubs' : rememberedSearchType();
  searching.value = true;
};
const searchPlaceholder = computed(() => ({
  clubs: 'Search clubs by name',
  people: 'User tag, or a name from your clubs',
  players: 'Search users by name or email',
})[searchType.value]);
const setSearchType = (type) => {
  searchType.value = type;
  searchValue.value = null;
  try {
    window.localStorage.setItem(SEARCH_TYPE_KEY, type);
  } catch {
    // Private window or blocked storage: the choice just lasts this open.
  }
  setTimeout(() => searchInput.value?.focus(), 50);
};
const route = useRoute();
const closeSearch = () => {
  searching.value = false;
};

// Focus the field once the dialog has transitioned in (autofocus is unreliable on a
// conditionally-mounted dialog).
watch(searching, async (open) => {
  if (open) {
    await nextTick();
    setTimeout(() => searchInput.value?.focus(), 120);
  }
});

watch(route, (to) => {
  searching.value = false;
  searchValue.value = null;
}, {immediate: true});

const { notifyUpdated } = useTimelineFeed();

const onTimelineUpdated = (obj) => {
  console.log('Timeline updated:', obj);
  // The feed regenerates server-side; flag it so the timeline offers a refresh
  // instead of yanking the list around mid-scroll.
  notifyUpdated();
}

const { on, off } = useSignalR()

// Note: SignalR connection is managed globally in useAuthService based on authentication state
// We only register/unregister event handlers here for timeline updates
onMounted(() => {
  on('timelineupdated', onTimelineUpdated);
})
onUnmounted(() => {
  off('timelineupdated', onTimelineUpdated)
})


</script>

<template>
  <v-app style="z-index: 0" class="">
    <alert-display />
    <v-app-bar color="white" :flat="true" scroll-behavior="elevate" class="pl-md-2 tl-topbar">
      <v-btn class="tl-iconbtn ma-0 ml-2 d-md-none" :icon="true" @click.stop="drawer = !drawer">
        <v-icon>mdi-menu</v-icon>
      </v-btn>
      <NuxtLink class="logo d-none d-sm-flex" to="/">
        <nuxt-img src="/images/logo.png" format="webp" width="600" height="150"  sizes="300px" alt="Tap Leagues Logo"/>
      </NuxtLink>
      <NuxtLink class="logo-thin d-sm-none" to="/">
        <nuxt-img src="/images/logo_thin.png" format="webp" width="325" height="150"  sizes="150px" alt="Tap Leagues Logo"/>
      </NuxtLink>
      <v-spacer class="d-flex"></v-spacer>
      <v-btn v-if="isSuperUser" :icon="true" class="tl-iconbtn mr-2" @click="searching = true; searchType = 'players'">
        <v-icon size="20">mdi-account-search-outline</v-icon>
      </v-btn>
      <v-btn v-if="isAuthenticated" :icon="true" class="tl-iconbtn mr-2" @click="openSearch">
        <v-icon size="20">mdi-magnify</v-icon>
      </v-btn>
      <assistant-launch-button/>
      <v-btn v-if="isAuthenticated" :icon="true" class="tl-iconbtn mr-2" @click="openMessaging">
        <v-badge
          :content="unreadCount"
          :model-value="unreadCount > 0"
          color="primary"
          overlap
          class="messaging-badge"
        >
          <v-icon size="20">mdi-message-outline</v-icon>
        </v-badge>
      </v-btn>
      <main-menu />
      <user-top-right v-if="isAuthenticated" />
    </v-app-bar>

    <v-navigation-drawer style="z-index:1010" v-model="drawer" location="left" :temporary="true" width="290" class="tl-drawer">
      <!-- Header: menu label + close -->
      <div class="tl-drawer-head">
        <span class="tl-drawer-title">Menu</span>
        <button type="button" class="tl-drawer-close" aria-label="Close menu" @click="drawer = false">
          <v-icon size="22">mdi-close</v-icon>
        </button>
      </div>

      <!-- Primary nav -->
      <nav class="tl-drawer-nav">
        <NuxtLink
            v-for="item in drawerNav"
            :key="item.title"
            :to="item.link"
            class="tl-drawer-link"
            @click="drawer = false"
        >
          <span class="tl-drawer-ic"><v-icon size="20">{{ drawerIcon(item) }}</v-icon></span>
          <span class="tl-drawer-label">{{ item.title }}</span>
          <v-icon size="18" class="tl-drawer-chev">mdi-chevron-right</v-icon>
        </NuxtLink>
      </nav>

      <!-- Auth actions (guests) -->
      <div v-if="!isAuthenticated" class="tl-drawer-auth">
        <v-btn block color="primary" size="large" rounded="pill" prepend-icon="mdi-account-plus-outline"
               :to="drawerSignUp" @click="drawer = false">Sign up</v-btn>
        <v-btn block variant="outlined" size="large" rounded="pill" class="mt-2 tl-drawer-signin"
               :to="drawerSignIn" @click="drawer = false">Sign in</v-btn>
        <p class="tl-drawer-note">Free for players and clubs* · no card required<commission-note/></p>
      </div>
    </v-navigation-drawer>

    <v-main class="bg-light">
      <div v-if="isLoading" class="loading-indicator d-flex flex-col align-center justify-center">
        <v-progress-circular :size="64" :width="7" :indeterminate="true" color="white" />
      </div>
      <v-dialog v-model="searching" :fullscreen="true" :scrollable="true" :hide-overlay="true"
                :retain-focus="false" :capture-focus="false" transition="dialog-bottom-transition">
        <div class="search-sheet">
          <!-- Sticky search header -->
          <div class="search-head">
            <div class="search-field" :class="{'search-field--modes': searchType !== 'players' && searchModes.length}">
              <!-- What to search, joined to the front of the box. The super admin's user search has no choice. -->
              <v-menu v-if="searchType !== 'players' && searchModes.length" location="bottom start" :offset="8">
                <template #activator="{props: menu}">
                  <button type="button" class="search-mode" v-bind="menu" aria-label="What to search">
                    <v-icon size="18">{{ currentMode.icon }}</v-icon>
                    <span class="search-mode-label">{{ currentMode.label }}</span>
                    <v-icon size="16" class="search-mode-caret">mdi-chevron-down</v-icon>
                  </button>
                </template>
                <v-list density="compact" class="search-mode-list" rounded="lg">
                  <v-list-item v-for="m in searchModes" :key="m.value" :active="searchType === m.value" rounded="lg" @click="setSearchType(m.value)">
                    <template #prepend><v-icon size="18">{{ m.icon }}</v-icon></template>
                    <v-list-item-title>{{ m.label }}</v-list-item-title>
                    <v-list-item-subtitle>{{ m.hint }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </v-menu>
              <v-icon v-else size="20" class="search-field-ic">mdi-magnify</v-icon>
              <input
                  ref="searchInput"
                  class="search-field-input"
                  type="text"
                  v-model="searchValue"
                  :placeholder="searchPlaceholder"
                  autocapitalize="none"
                  autocorrect="off"
                  spellcheck="false"
                  @keydown.esc="closeSearch"
              />
              <v-btn v-if="searchValue" :icon="true" size="26" variant="text" class="search-field-clear"
                     @click="searchValue = null">
                <v-icon size="18">mdi-close-circle</v-icon>
              </v-btn>
            </div>
            <v-btn :icon="true" size="42" variant="text" class="search-close" @click="closeSearch">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <!-- Results / states -->
          <div class="search-body">
            <div class="search-col">
              <template v-if="searchType === 'people'">
                <div class="search-hint">People in your clubs and people you follow come up by name. Anyone else needs their exact user tag, which is on their profile.</div>
                <people-search-results v-if="searchValue" :query="searchValue"/>
                <div v-else class="search-state">
                  <div class="search-state-ic search-state-ic--brand"><v-icon size="26">mdi-account-heart-outline</v-icon></div>
                  <div class="search-state-title">Find a friend</div>
                  <div class="search-state-sub">Type a name from your clubs, or a friend's exact user tag to follow someone from another club. Both of you have to agree.</div>
                </div>
              </template>
              <template v-else-if="searchValue != null && searchValue.length > 0">
                <crud-list-loader :debounced="true" :path="'api/v2/'+searchType"
                                  :custom-filters="{ keywords: searchValue }">
                  <template #empty>
                    <div class="search-state">
                      <div class="search-state-ic"><v-icon size="26">mdi-magnify-close</v-icon></div>
                      <div class="search-state-title">No {{ searchType === 'clubs' ? 'clubs' : 'users' }} found</div>
                      <div class="search-state-sub">
                        Nothing matched “{{ searchValue }}”. Try a different spelling{{ searchType === 'clubs' ? '' : ' or their email' }}.
                      </div>
                    </div>
                  </template>
                  <template #default="searchSlot">
                    <div class="search-reslabel">{{ searchType === 'clubs' ? 'Clubs' : 'Users' }}</div>
                    <club-search-list v-if="searchType === 'clubs'" :items="searchSlot.items"/>
                    <player-list v-else :items="searchSlot.items"/>
                  </template>
                </crud-list-loader>
              </template>

              <!-- Empty query prompt -->
              <div v-else class="search-state">
                <div class="search-state-ic search-state-ic--brand">
                  <v-icon size="26">{{ searchType === 'clubs' ? 'mdi-account-group-outline' : 'mdi-account-search-outline' }}</v-icon>
                </div>
                <div class="search-state-title">{{ searchType === 'clubs' ? 'Find a club' : 'Find a user' }}</div>
                <div class="search-state-sub">
                  Start typing to search {{ searchType === 'clubs' ? 'clubs by name' : 'users by name or email' }}.
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-dialog>
      <slot />
      <client-only><mfa-offer-prompt/></client-only>
    </v-main>

    <!-- neutral footer — brand red is reserved for meaning, not chrome.
         No absolute/app props: layout registration is broken app-wide (vars stay 0),
         which made the absolute footer paint over page content; flowing after v-main
         can't overlap anything. -->
    <v-footer v-if="footer" class="pa-7 bg-white w-100 tl-footer d-block flex-grow-0">
      <main-footer />
    </v-footer>
  </v-app>
</template>

<style scoped lang="scss">
/* ===== App-bar search dialog ===== */
.search-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  background: rgb(var(--v-theme-light));
}

.search-head {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: rgb(var(--v-theme-surface));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.search-field {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  max-width: 620px;
  margin: 0 auto;
  padding: 0 8px 0 14px;
  height: 46px;
  border-radius: 999px;
  background: rgb(var(--v-theme-light));
  border: 1px solid rgb(var(--v-theme-border));
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: rgb(var(--v-theme-primary));
    box-shadow: 0 0 0 3px rgba(211, 41, 70, 0.08);
  }
}

.search-field-ic {
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

.search-field-input {
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: rgb(var(--v-theme-text));

  &::placeholder {
    color: rgb(var(--v-theme-textSubtle));
  }
}

.search-field-clear {
  color: rgb(var(--v-theme-textSubtle));
  flex-shrink: 0;
}

.search-close {
  flex-shrink: 0;
  color: rgb(var(--v-theme-charcoal));
}

.search-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 16px 48px;
}

.search-col {
  max-width: 620px;
  margin: 0 auto;
}

.search-field--modes { padding-left: 4px; }
.search-mode {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 34px;
  padding: 0 8px 0 10px;
  margin-right: 2px;
  border: 0;
  border-right: 1px solid rgb(var(--v-theme-border));
  border-radius: 0;
  background: none;
  font-size: 13.5px;
  font-weight: 600;
  color: rgb(var(--v-theme-text));
  cursor: pointer;
  flex-shrink: 0;
}
.search-mode-caret { color: rgb(var(--v-theme-textSubtle)); }
.search-mode-list { min-width: 230px; padding: 6px; }
/* Vuetify leaves a 32px spacer after a prepend icon; the icon sits 10px from the words here. */
.search-mode-list :deep(.v-list-item__spacer) { width: 10px; }
.search-mode-list :deep(.v-list-item__prepend .v-icon) { opacity: 1; color: rgb(var(--v-theme-textSubtle)); }
.search-mode-list :deep(.v-list-item--active .v-list-item__prepend .v-icon) { color: rgb(var(--v-theme-primary)); }
.search-mode-list :deep(.v-list-item-subtitle) { font-size: 12px; }
.search-hint { font-size: 12.5px; color: rgb(var(--v-theme-textSubtle)); margin: 0 2px 8px; }
.search-reslabel {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
  margin: 2px 0 10px 4px;
}

/* Empty / prompt states */
.search-state {
  text-align: center;
  padding: 56px 20px;
  max-width: 380px;
  margin: 0 auto;
}

.search-state-ic {
  width: 56px;
  height: 56px;
  margin: 0 auto 16px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-textSubtle));

  &--brand {
    background: rgb(var(--v-theme-brandTint));
    border-color: transparent;
    color: rgb(var(--v-theme-primary));
  }
}

.search-state-title {
  font-size: 16px;
  font-weight: 700;
  color: rgb(var(--v-theme-text));
}

.search-state-sub {
  font-size: 13.5px;
  line-height: 1.5;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 6px;
}

/* Same styles as before */
.loading-indicator {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0.4;
  z-index: 3000;
}
.logo {
  // Removed display: inline-block to allow Vuetify's d-none/d-sm-flex utilities to work
  padding: 3px;
  margin-top: 0px;
  img {
    width: 170px;
    height: auto;
  }
}
.logo-thin {
  // Removed display: inline-block to allow Vuetify's d-sm-none utility to work
  padding: 3px;
  margin-top: 5px;
  img {
    width: 70px;
    height: auto;
  }
}
.nav-header {
  margin-top: 16px;
  border-top: solid 1px rgb(var(--v-theme-border));
  padding-top: 16px;
  margin-bottom: 6px;
  min-height: 20px;
  * {
    font-weight: bold;
    font-size: 12px;
    line-height: 14px !important;
  }
  &:first-child {
    border-top: none;
    margin-top: 0;
  }
}

/* ── Mobile slide-out drawer ── */
.tl-drawer :deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
}

.tl-drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.tl-drawer-title {
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: rgb(var(--v-theme-textSubtle));
}

.tl-drawer-close {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: rgb(var(--v-theme-textSubtle));
  border: 1px solid rgb(var(--v-theme-borderLight));

  &:hover {
    background: rgb(var(--v-theme-light));
  }
}

.tl-drawer-nav {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 12px 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.tl-drawer-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border-radius: 12px;
  text-decoration: none;
  color: rgb(var(--v-theme-text));
  font-size: 14.5px;
  font-weight: 600;
  transition: background 0.12s ease, color 0.12s ease;

  .tl-drawer-ic {
    width: 34px;
    height: 34px;
    border-radius: 10px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    background: rgb(var(--v-theme-light));
    color: rgb(var(--v-theme-charcoal));
    transition: background 0.12s ease, color 0.12s ease;
  }

  .tl-drawer-label {
    flex: 1 1 auto;
    min-width: 0;
  }

  .tl-drawer-chev {
    color: rgb(var(--v-theme-textSubtle));
    opacity: 0;
    transition: opacity 0.12s ease;
  }

  &:hover {
    background: rgb(var(--v-theme-light));

    .tl-drawer-chev {
      opacity: 1;
    }
  }

  &.router-link-exact-active {
    background: rgb(var(--v-theme-brandTint));
    color: rgb(var(--v-theme-primary));

    .tl-drawer-ic {
      background: rgb(var(--v-theme-primary));
      color: #fff;
    }

    .tl-drawer-chev {
      opacity: 1;
      color: rgb(var(--v-theme-primary));
    }
  }
}

.tl-drawer-auth {
  padding: 14px 16px calc(14px + env(safe-area-inset-bottom));
  border-top: 1px solid rgb(var(--v-theme-borderLight));
}

.tl-drawer-signin {
  border-color: rgb(var(--v-theme-borderLight));
}

.tl-drawer-note {
  font-size: 11px;
  color: rgb(var(--v-theme-textSubtle));
  text-align: center;
  margin-top: 10px;
}

.messaging-badge :deep(.v-badge__badge) {
  font-size: 10px;
  font-weight: bold;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
}

/* hairline-bordered square icon buttons (design kit .iconbtn) */
.tl-iconbtn {
  width: 40px !important;
  height: 40px !important;
  border-radius: 12px !important;
  border: 1px solid rgb(var(--v-theme-borderLight));
  color: rgb(var(--v-theme-text));
}

.tl-topbar {
  border-bottom: 1px solid rgb(var(--v-theme-borderLight)) !important;
}

.tl-footer {
  border-top: 1px solid rgb(var(--v-theme-borderLight)) !important;
}
</style>
